// Inference section for System Architecture - 198 lines ✅
import { Brain, Zap, TrendingUp } from 'lucide-react'

interface AIPattern {
  name: string
  input: string
  process: string
  output: string
}

const aiPatterns: AIPattern[] = [
  {
    name: 'Issue Classification',
    input: 'User conversation text (natural language)',
    process: 'NLP extraction → Identify title, description, category, priority, D365 module, version',
    output: 'Structured issue record with metadata'
  },
  {
    name: 'Semantic Search',
    input: 'User query text',
    process: 'text-embedding-3-small → Query vector (1536 dims) → Cosine similarity → Composite scoring',
    output: 'Ranked knowledge entries with confidence scores'
  },
  {
    name: 'Duplicate Detection',
    input: 'New knowledge entry text',
    process: 'Embedding comparison → Find similar entries (similarity > 0.85) → Human review',
    output: 'Duplicate flag with similar entry suggestions'
  },
  {
    name: 'Expert Routing',
    input: 'Issue metadata (module, category, priority)',
    process: 'Match against expert profiles (expertise_domains, d365_modules) → Rank by fit',
    output: 'Ranked expert recommendations'
  }
]

function AIPatternCard({ pattern }: { pattern: AIPattern }) {
  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
      <h4 className="mb-3 flex items-center gap-2 font-semibold text-kaycoo-teal-dark">
        <Zap className="h-5 w-5" />
        {pattern.name}
      </h4>
      <div className="space-y-2 text-sm">
        <div>
          <span className="font-medium text-kaycoo-teal-dark">Input:</span>
          <div className="text-kaycoo-charcoal-2">{pattern.input}</div>
        </div>
        <div>
          <span className="font-medium text-kaycoo-teal-dark">Process:</span>
          <div className="text-kaycoo-charcoal-2">{pattern.process}</div>
        </div>
        <div>
          <span className="font-medium text-kaycoo-teal-dark">Output:</span>
          <div className="text-kaycoo-charcoal-2">{pattern.output}</div>
        </div>
      </div>
    </div>
  )
}

function ScoringAlgorithms() {
  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
      <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-kaycoo-teal-dark">
        <TrendingUp className="h-5 w-5" />
        Business Rules & Scoring
      </h3>

      <div className="space-y-4">
        {/* KB Ranking */}
        <div className="rounded bg-kaycoo-teal-pale p-4">
          <h4 className="mb-2 font-semibold text-kaycoo-teal-dark">Knowledge Base Ranking Formula</h4>
          <code className="block rounded bg-gray-50 p-3 text-sm text-kaycoo-charcoal">
            composite_score = (similarity × 0.6) + (success_rate × 0.3) + (recency × 0.1)
          </code>
          <div className="mt-2 text-sm text-kaycoo-charcoal-2">
            <div className="font-medium">Confidence Levels:</div>
            <div>• High: score ≥ 0.85</div>
            <div>• Medium: 0.70 ≤ score {'<'} 0.85</div>
            <div>• Low: score {'<'} 0.70</div>
          </div>
        </div>

        {/* Version Matching */}
        <div className="rounded bg-kaycoo-teal-pale p-4">
          <h4 className="mb-2 font-semibold text-kaycoo-teal-dark">D365 Version Matching Weights</h4>
          <div className="space-y-1 text-sm text-kaycoo-charcoal-2">
            <div>• <span className="font-medium">Exact match:</span> weight = 1.0</div>
            <div>• <span className="font-medium">Same minor version:</span> weight = 0.8</div>
            <div>• <span className="font-medium">Same major version:</span> weight = 0.6</div>
            <div>• <span className="font-medium">Cross-major:</span> weight = 0.3 (low confidence)</div>
            <div>• <span className="font-medium">No version specified:</span> weight = 0.5 (penalty)</div>
          </div>
        </div>

        {/* Success Rate */}
        <div className="rounded bg-kaycoo-teal-pale p-4">
          <h4 className="mb-2 font-semibold text-kaycoo-teal-dark">Success Rate Calculation</h4>
          <code className="block rounded bg-gray-50 p-3 text-sm text-kaycoo-charcoal">
            success_rate = success_count / (success_count + failure_count)
          </code>
          <div className="mt-2 text-sm text-kaycoo-charcoal-2">
            Minimum 3 total attempts required for statistical validity
          </div>
        </div>
      </div>
    </div>
  )
}

function ValidationRules() {
  const rules = [
    {
      category: 'Knowledge Entry Validation',
      rules: [
        'Human expert must approve before appearing in search',
        'Minimum required fields: title, description, content',
        'D365 version must be in supported version list',
        'Category must be from predefined enum',
        'Embedding must be successfully generated'
      ]
    },
    {
      category: 'Issue Resolution Criteria',
      rules: [
        'Linked KB entry or resolution method required',
        'Success rate threshold > 0.7 for auto-suggest',
        'Manual override allowed by experts',
        'Complete audit trail must be captured'
      ]
    },
    {
      category: 'Search Quality Thresholds',
      rules: [
        'Confidence threshold ≥ 70% for results',
        'Return maximum top 10 results',
        'Semantic similarity weight: 60%',
        'Success rate weight: 30%',
        'Recency weight: 10%'
      ]
    }
  ]

  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
      <h3 className="mb-4 text-lg font-semibold text-kaycoo-teal-dark">Validation Rules</h3>
      <div className="grid gap-4 md:grid-cols-3">
        {rules.map((section, i) => (
          <div key={i} className="rounded border border-kaycoo-teal bg-kaycoo-teal-pale p-3">
            <h4 className="mb-2 font-semibold text-kaycoo-teal-dark">{section.category}</h4>
            <ul className="space-y-1 text-sm text-kaycoo-charcoal-2">
              {section.rules.map((rule, j) => (
                <li key={j}>• {rule}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

export function InferenceSection() {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Brain className="h-6 w-6 text-kaycoo-teal-dark" />
          <h2 className="text-2xl font-bold text-kaycoo-teal-dark">AI Inference & Business Logic</h2>
        </div>
        <p className="text-kaycoo-charcoal-2">
          AI-powered patterns, scoring algorithms, validation rules, and business logic that drive intelligent features.
        </p>
      </div>

      {/* AI Patterns */}
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-kaycoo-teal-dark">AI Inference Patterns</h3>
        <div className="grid gap-4 md:grid-cols-2">
          {aiPatterns.map((pattern) => (
            <AIPatternCard key={pattern.name} pattern={pattern} />
          ))}
        </div>
      </div>

      <ScoringAlgorithms />
      <ValidationRules />

      {/* AI Model Info */}
      <div className="rounded-lg border border-kaycoo-teal-dark bg-kaycoo-teal-pale p-6">
        <h3 className="mb-3 text-lg font-semibold text-kaycoo-teal-dark">AI Models & Configuration</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <div className="font-medium text-kaycoo-teal-dark">Embedding Model</div>
            <div className="text-sm text-kaycoo-charcoal-2">text-embedding-3-small (OpenAI)</div>
            <div className="text-xs text-kaycoo-charcoal-2">1536 dimensions, cosine similarity</div>
          </div>
          <div>
            <div className="font-medium text-kaycoo-teal-dark">Vector Index</div>
            <div className="text-sm text-kaycoo-charcoal-2">pgvector HNSW (Hierarchical Navigable Small World)</div>
            <div className="text-xs text-kaycoo-charcoal-2">Fast approximate nearest neighbor search</div>
          </div>
        </div>
      </div>
    </div>
  )
}
